<template>
  <div class="container-fluid py-4">
    <div class="mb-4">
      <h2 class="mb-0">Seguridad - Autenticacion en dos pasos</h2>
      <p class="text-muted mb-0">Protege tu cuenta admin con Google Authenticator o similar</p>
    </div>

    <div v-if="error" class="alert alert-danger">{{ error }}</div>

    <div class="card" style="max-width: 640px;">
      <div class="card-body">
        <div v-if="cargando" class="text-center py-4">
          <div class="spinner-border" role="status"></div>
        </div>

        <!-- 2FA activo -->
        <div v-else-if="activo">
          <div class="alert alert-success">
            <i class="bi bi-shield-check me-2"></i>
            La autenticacion en dos pasos esta <strong>activa</strong>.
            Al iniciar sesion se pedira un codigo de tu app.
          </div>
          <hr>
          <h6>Desactivar 2FA</h6>
          <p class="text-muted small">Ingresa un codigo actual de tu app para confirmar.</p>
          <div class="d-flex gap-2">
            <input
              v-model="codigo"
              type="text"
              class="form-control w-auto"
              maxlength="6"
              placeholder="000000"
              inputmode="numeric"
            >
            <button class="btn btn-outline-danger" @click="desactivar" :disabled="procesando || codigo.length !== 6">
              Desactivar
            </button>
          </div>
        </div>

        <!-- Configuracion pendiente: mostrar secreto -->
        <div v-else-if="secreto">
          <h6>1. Agrega la cuenta en tu app de autenticacion</h6>
          <p class="text-muted small mb-2">
            Abre Google Authenticator (o Authy, Microsoft Authenticator) y elige
            <strong>Ingresar clave de configuracion</strong>. Usa estos datos:
          </p>
          <div class="p-3 bg-light rounded mb-3">
            <div class="mb-1"><strong>Cuenta:</strong> SICECOM</div>
            <div class="d-flex align-items-center gap-2">
              <strong>Clave:</strong>
              <code class="fs-6 user-select-all">{{ secretoFormateado }}</code>
              <button class="btn btn-sm btn-outline-secondary" @click="copiarSecreto" title="Copiar">
                <i class="bi bi-clipboard"></i>
              </button>
            </div>
            <div class="small text-muted mt-1">Tipo: basado en tiempo (TOTP)</div>
          </div>

          <h6>2. Confirma con el codigo generado</h6>
          <div class="d-flex gap-2">
            <input
              v-model="codigo"
              type="text"
              class="form-control w-auto"
              maxlength="6"
              placeholder="000000"
              inputmode="numeric"
            >
            <button class="btn btn-success" @click="activar" :disabled="procesando || codigo.length !== 6">
              <i class="bi bi-check-lg"></i> Activar 2FA
            </button>
          </div>
        </div>

        <!-- 2FA inactivo -->
        <div v-else>
          <div class="alert alert-warning">
            <i class="bi bi-shield-exclamation me-2"></i>
            La autenticacion en dos pasos esta <strong>desactivada</strong>.
          </div>
          <p class="text-muted">
            Con 2FA activo, ademas de tu contrasena se pedira un codigo de 6 digitos
            generado por tu telefono. Esto protege tu cuenta aunque roben tu contrasena.
          </p>
          <button class="btn btn-primary" @click="generar" :disabled="procesando">
            <i class="bi bi-shield-plus"></i> Configurar 2FA
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/services/api'
import { Toast } from '@/utils/notifications'
import themeMixin from '@/mixins/themeMixin'

export default {
  mixins: [themeMixin],
  name: 'SeguridadDosFactores',
  data() {
    return {
      cargando: true,
      procesando: false,
      error: null,
      activo: false,
      secreto: null,
      codigo: ''
    }
  },
  computed: {
    secretoFormateado() {
      return (this.secreto || '').replace(/(.{4})/g, '$1 ').trim()
    }
  },
  async mounted() {
    await this.cargarEstado()
  },
  methods: {
    async cargarEstado() {
      this.cargando = true
      this.error = null
      try {
        const res = await api.get('/admin/2fa/estado')
        this.activo = res.data?.datos?.activo || false
      } catch (e) {
        this.error = 'Error al consultar el estado: ' + (e.response?.data?.error || e.message)
      } finally {
        this.cargando = false
      }
    },
    async generar() {
      this.procesando = true
      try {
        const res = await api.post('/admin/2fa/generar')
        if (res.data?.exito) {
          this.secreto = res.data.datos.secreto
          this.codigo = ''
        } else {
          Toast.error(res.data?.error || 'Error al generar el secreto')
        }
      } catch (e) {
        Toast.error(e.response?.data?.error || 'Error al generar el secreto')
      } finally {
        this.procesando = false
      }
    },
    async activar() {
      this.procesando = true
      try {
        const res = await api.post('/admin/2fa/activar', { codigo: this.codigo })
        if (res.data?.exito) {
          Toast.success('2FA activado. Se pedira el codigo en cada inicio de sesion.')
          this.activo = true
          this.secreto = null
          this.codigo = ''
        } else {
          Toast.error(res.data?.error || 'Codigo incorrecto')
        }
      } catch (e) {
        Toast.error(e.response?.data?.error || 'Codigo incorrecto')
      } finally {
        this.procesando = false
      }
    },
    async desactivar() {
      this.procesando = true
      try {
        const res = await api.post('/admin/2fa/desactivar', { codigo: this.codigo })
        if (res.data?.exito) {
          Toast.success('2FA desactivado')
          this.activo = false
          this.secreto = null
          this.codigo = ''
        } else {
          Toast.error(res.data?.error || 'Codigo incorrecto')
        }
      } catch (e) {
        Toast.error(e.response?.data?.error || 'Codigo incorrecto')
      } finally {
        this.procesando = false
      }
    },
    async copiarSecreto() {
      try {
        await navigator.clipboard.writeText(this.secreto)
        Toast.success('Clave copiada')
      } catch {
        Toast.error('No se pudo copiar')
      }
    }
  }
}
</script>

<style scoped>
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}
</style>
